import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Upload,
  Space,
  Select,
  TimePicker,
  message,
  Row,
  Col,
  Divider,
  Avatar,
  Typography,
  Tag,
  Spin,
  Alert,
  DatePicker,
} from "antd";
import {
  UserOutlined,
  CameraOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  CalendarOutlined,
  HomeOutlined,
  FileProtectOutlined,
  TrophyOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { doctorAPI, doctorSettingsAPI, userAPI } from "../../services/api";
import "./DoctorSettings.css";
import { useParams } from "react-router-dom";

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const DoctorSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [timings, setTimings] = useState([
    { startTime: null, endTime: null, id: Date.now() },
  ]);
  const { doctorId: doctor } = useParams();
  const [specialties, setSpecialties] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [services, setServices] = useState([]);
  const [mapLocation, setMapLocation] = useState({
    latitude: "",
    longitude: "",
    address: "",
  });
  const [doctorData, setDoctorData] = useState(null);
  const [doctorId, setDoctorId] = useState(null);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);

  useEffect(() => {
    fetchDoctorProfile();
    doctorSettingsAPI.getSettings()
      .then((res) => setSpecialtyOptions(res.data?.specialties || []))
      .catch(() => {});
  }, []);

  const fetchDoctorProfile = async () => {
    try {
      setLoading(true);
      const userId = doctor;

      // 1. Always fetch user record — this is where admin stores the core fields
      const userRes = await userAPI.getById(userId);
      const userData = userRes?.data?.data || userRes?.data || {};

      // Populate read-only fields from user model
      form.setFieldsValue({
        name: userData.name || "",
        email: userData.email || "",
        phone: userData.phone || "",
        qualifications: userData.qualifications || "",
        consultationFee: userData.consultationFee,
        experience: userData.experience,
      });
      if (userData.profileImage) setImageUrl(userData.profileImage);
      if (userData.specialties?.length) setSpecialties(userData.specialties);

      // 2. Also try to fetch extended doctor profile (clinic, timings, etc.)
      try {
        const res = await doctorAPI.getByUserId(userId);
        if (res?.success && res.data) {
          const data = res.data;
          setDoctorData(data);
          setDoctorId(data._id);
          setIsNewProfile(false);

          // Extended fields from doctor profile (override user model values if set)
          form.setFieldsValue({
            consultationFee: data.consultationFee ?? userData.consultationFee,
            experience: data.experience ?? userData.experience,
            medicalRegistrationNumber: data.medicalRegistrationNumber,
            registrationCouncil: data.registrationCouncil,
            registrationYear: data.registrationYear,
            clinicName: data.clinicName,
            about: data.about,
            ...(data.aadharCard ? { aadharNumber: data.aadharCard.number } : {}),
          });

          if (data.clinicLocation) {
            form.setFieldsValue({
              clinicAddress: data.clinicLocation.address,
              clinicCity: data.clinicLocation.city,
              clinicState: data.clinicLocation.state,
              clinicPincode: data.clinicLocation.pincode,
            });
            if (data.clinicLocation.coordinates) {
              setMapLocation({
                latitude: data.clinicLocation.coordinates.latitude || "",
                longitude: data.clinicLocation.coordinates.longitude || "",
                address: data.clinicLocation.address || "",
              });
            }
          }

          if (data.specialties?.length) setSpecialties(data.specialties);
          if (data.languages?.length) setLanguages(data.languages);
          if (data.services?.length) setServices(data.services);

          if (data.timings?.length) {
            setTimings(
              data.timings.map((t, i) => ({
                startTime: t.startTime ? dayjs(t.startTime, "HH:mm") : null,
                endTime: t.endTime ? dayjs(t.endTime, "HH:mm") : null,
                id: i,
              }))
            );
          }
        } else {
          setIsNewProfile(true);
        }
      } catch (profileErr) {
        if (profileErr.response?.status !== 404) {
          console.error("Doctor profile fetch error:", profileErr);
        }
        setIsNewProfile(true);
      }
    } catch (error) {
      message.error("Failed to fetch doctor data");
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return false;
    }

    setProfileImage(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    return false;
  };

  const uploadProfileImage = async (userId) => {
    if (!profileImage) return null;

    try {
      const formData = new FormData();
      formData.append("image", profileImage);
      // Use admin endpoint: POST /api/users/:id/profile-image
      const res = await userAPI.uploadProfileImage(userId, formData);
      return res.data?.data?.profileImage || res.data?.profileImage || imageUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const addTiming = () => {
    if (timings.length >= 5) {
      message.warning("Maximum 5 time slots allowed");
      return;
    }
    setTimings([
      ...timings,
      { startTime: null, endTime: null, id: Date.now() },
    ]);
  };

  const removeTiming = (id) => {
    if (timings.length === 1) {
      message.warning("At least one time slot is required");
      return;
    }
    setTimings(timings.filter((timing) => timing.id !== id));
  };

  const updateTiming = (id, field, value) => {
    setTimings(
      timings.map((timing) =>
        timing.id === id ? { ...timing, [field]: value } : timing
      )
    );
  };

  const addToArray = (value, array, setArray, maxLength = 10) => {
    if (value && !array.includes(value)) {
      if (array.length >= maxLength) {
        message.warning(`Maximum ${maxLength} items allowed`);
        return;
      }
      setArray([...array, value]);
    }
  };

  const removeFromArray = (item, array, setArray) => {
    setArray(array.filter((i) => i !== item));
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);

      // Validate timings
      const hasEmptyTimings = timings.some(
        (timing) => !timing.startTime || !timing.endTime
      );
      if (hasEmptyTimings) {
        message.error("Please complete all time slots");
        document.querySelector(".timing-card:has(.ant-picker-status-error)")?.scrollIntoView({ block: "center", behavior: "smooth" });
        setSaving(false);
        return;
      }

      const userId = doctor;

      // Upload image if changed
      let uploadedImageUrl = imageUrl;
      if (profileImage) {
        uploadedImageUrl = await uploadProfileImage(userId);
      }

      // Format timings
      const formattedTimings = timings.map((timing) => ({
        startTime: timing.startTime.format("HH:mm"),
        endTime: timing.endTime.format("HH:mm"),
      }));

      // Prepare doctor data
      const doctorData = {
        userId,
        name: values.name,
        qualifications: values.qualifications,
        specialties,
        experience: values.experience,
        consultationFee: values.consultationFee,
        medicalRegistrationNumber: values.medicalRegistrationNumber,
        registrationCouncil: values.registrationCouncil,
        registrationYear: values.registrationYear,
        aadharCard: {
          number: values.aadharNumber,
        },
        clinicName: values.clinicName,
        clinicLocation: {
          address: values.clinicAddress,
          coordinates: {
            latitude: parseFloat(mapLocation.latitude) || null,
            longitude: parseFloat(mapLocation.longitude) || null,
          },
          city: values.clinicCity,
          state: values.clinicState,
          pincode: values.clinicPincode,
        },
        timings: formattedTimings,
        languages,
        services,
        about: values.about,
        profileImage: uploadedImageUrl,
      };

      let response;
      if (isNewProfile) {
        response = await doctorAPI.create(doctorData);
        message.success("Doctor profile created successfully!");
      } else {
        response = await doctorAPI.updateByUserId(userId, doctorData);
        message.success("Profile updated successfully!");
      }

      // Refresh data
      await fetchDoctorProfile();
    } catch (error) {
      const detail = error.response?.data?.error || error.response?.data?.message || error.message;
      if (/medicalRegistrationNumber/i.test(detail || '')) {
        form.setFields([{ name: 'medicalRegistrationNumber', errors: ['Check this registration number; it may already be in use.'] }]);
        form.scrollToField('medicalRegistrationNumber', { block: 'center', focus: true });
      }
      message.error(detail || 'Unable to save this profile.');
      console.error("Error saving profile:", error);
    } finally {
      setSaving(false);
    }
  };

  const getLocationFromBrowser = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapLocation({
            ...mapLocation,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          });
          message.success("Location captured successfully!");
        },
        (error) => {
          message.error(
            "Failed to get location. Please enable location services."
          );
          console.error("Geolocation error:", error);
        }
      );
    } else {
      message.error("Geolocation is not supported by your browser");
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <Text style={{ marginTop: 16 }}>Loading profile...</Text>
      </div>
    );
  }

  return (
    <div className="doctor-settings-container">
      <div className="settings-header">
        <Title level={2}>
          <MedicineBoxOutlined /> Doctor Settings
        </Title>
        <Text type="secondary">
          {isNewProfile
            ? "Create your professional profile"
            : "Manage your profile and preferences"}
        </Text>
      </div>

      {isNewProfile && (
        <Alert
          message="Complete Your Profile"
          description="Please fill in all the required information to create your doctor profile and start accepting appointments."
          type="info"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        scrollToFirstError={{ behavior: "smooth", block: "center", focus: true }}
        className="settings-form"
        scrollToFirstError
      >
        {/* Profile Photo Section */}
        <Card className="settings-card photo-card" title="Profile Photo">
          <div className="profile-photo-section">
            <Avatar
              size={120}
              src={imageUrl}
              icon={<UserOutlined />}
              className="profile-avatar"
            />
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<CameraOutlined />} type="primary">
                Change Photo
              </Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              Recommended: Square image, max 5MB
            </Text>
          </div>
        </Card>

        {/* Basic Information — set by admin, read-only */}
        <Card
          className="settings-card"
          title={
            <>
              <UserOutlined /> Basic Information
            </>
          }
          extra={<Text type="secondary" style={{ fontSize: 12 }}>Managed by admin</Text>}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Full Name" name="name">
                <Input
                  prefix={<UserOutlined />}
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Email" name="email">
                <Input
                  prefix={<MailOutlined />}
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Phone Number" name="phone">
                <Input
                  prefix={<PhoneOutlined />}
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Qualifications" name="qualifications">
                <Input
                  prefix={<IdcardOutlined />}
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Professional Information */}
        <Card
          className="settings-card"
          title={
            <>
              <MedicineBoxOutlined /> Professional Information
            </>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Consultation Fee (₹)"
                name="consultationFee"
                rules={[
                  { required: true, message: "Please enter consultation fee" },
                ]}
              >
                <InputNumber
                  prefix="₹"
                  placeholder="500"
                  size="large"
                  style={{ width: "100%" }}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Experience (Years)"
                name="experience"
                rules={[{ required: true, message: "Please enter experience" }]}
              >
                <InputNumber
                  placeholder="10"
                  size="large"
                  style={{ width: "100%" }}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Specialties">
                <Space orientation="vertical" style={{ width: "100%" }}>
                  <Select
                    placeholder="Add specialty"
                    size="large"
                    style={{ width: "100%" }}
                    onSelect={(value) =>
                      addToArray(value, specialties, setSpecialties)
                    }
                    value={null}
                  >
                    {specialtyOptions.map((spec) => (
                      <Option key={spec} value={spec}>{spec}</Option>
                    ))}
                  </Select>
                  <div className="specialty-tags">
                    {specialties.map((specialty) => (
                      <Tag
                        key={specialty}
                        closable
                        onClose={() =>
                          removeFromArray(
                            specialty,
                            specialties,
                            setSpecialties
                          )
                        }
                        color="blue"
                      >
                        {specialty}
                      </Tag>
                    ))}
                  </div>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Registration Details */}
        <Card
          className="settings-card"
          title={
            <>
              <FileProtectOutlined /> Registration Details
            </>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Medical Registration Number"
                name="medicalRegistrationNumber" rules={[{ required: true, message: "Enter medical registration number" }]}
              >
                <Input placeholder="MCI12345" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Registration Council"
                name="registrationCouncil" rules={[{ required: true, message: "Enter registration council" }]}
              >
                <Input placeholder="Medical Council of India" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Registration Year" name="registrationYear">
                <InputNumber
                  placeholder="2015"
                  size="large"
                  style={{ width: "100%" }}
                  min={1950}
                  max={new Date().getFullYear()}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Aadhar Card Number" name="aadharNumber">
                <Input
                  placeholder="1234 5678 9012"
                  size="large"
                  maxLength={12}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Consultation Timings */}
        <Card
          className="settings-card"
          title={
            <>
              <ClockCircleOutlined /> Consultation Timings
            </>
          }
          extra={
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addTiming}
              size="small"
            >
              Add Slot
            </Button>
          }
        >
          <Space orientation="vertical" style={{ width: "100%" }} size="middle">
            {timings.map((timing, index) => (
              <Card
                key={timing.id}
                size="small"
                className="timing-card"
                title={`Time Slot ${index + 1}`}
                extra={
                  timings.length > 1 && (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeTiming(timing.id)}
                      size="small"
                    />
                  )
                }
              >
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={24} sm={10}>
                    <Text strong style={{ fontSize: 12 }}>
                      Start Time
                    </Text>
                    <TimePicker
                      value={timing.startTime}
                      onChange={(time) =>
                        updateTiming(timing.id, "startTime", time)
                      }
                      format="hh:mm A"
                      use12Hours
                      size="large"
                      style={{ width: "100%" }}
                      placeholder="10:00 AM"
                      status={!timing.startTime ? "error" : undefined}
                    />
                  </Col>
                  <Col xs={24} sm={10}>
                    <Text strong style={{ fontSize: 12 }}>
                      End Time
                    </Text>
                    <TimePicker
                      value={timing.endTime}
                      onChange={(time) =>
                        updateTiming(timing.id, "endTime", time)
                      }
                      format="hh:mm A"
                      use12Hours
                      size="large"
                      style={{ width: "100%" }}
                      placeholder="12:00 PM"
                      status={!timing.endTime ? "error" : undefined}
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <div className="timing-preview">
                      {timing.startTime && timing.endTime && (
                        <Tag color="green">
                          {timing.startTime.format("h:mm A")} -{" "}
                          {timing.endTime.format("h:mm A")}
                        </Tag>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
          <Alert
            message="Note: You can add up to 5 consultation time slots per day"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>

        {/* Clinic Information */}
        <Card
          className="settings-card"
          title={
            <>
              <HomeOutlined /> Clinic Information
            </>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Form.Item label="Clinic Name" name="clinicName" rules={[{ required: true, message: "Enter clinic name" }]}>
                <Input placeholder="Apollo Clinic" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Clinic Address" name="clinicAddress" rules={[{ required: true, message: "Enter clinic address" }]}>
                <TextArea
                  placeholder="123, Main Street, Near City Mall"
                  rows={2}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="City" name="clinicCity" rules={[{ required: true, message: "Enter city" }]}>
                <Input placeholder="Mumbai" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="State" name="clinicState" rules={[{ required: true, message: "Enter state" }]}>
                <Input placeholder="Maharashtra" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Pincode" name="clinicPincode" rules={[{ required: true, message: "Enter pincode" }]}>
                <Input placeholder="400001" size="large" maxLength={6} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Location Coordinates */}
        <Card
          className="settings-card"
          title={
            <>
              <EnvironmentOutlined /> Location Coordinates
            </>
          }
          extra={
            <Button
              type="primary"
              icon={<EnvironmentOutlined />}
              onClick={getLocationFromBrowser}
              size="small"
            >
              Get My Location
            </Button>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Form.Item
                label="Paste Google Maps Link"
                extra="Open Google Maps → share → copy link. Paste it here to auto-fill coordinates."
              >
                <Input.Search
                  placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/..."
                  enterButton="Extract"
                  size="large"
                  allowClear
                  onSearch={(val) => {
                    if (!val) return;
                    // Try @lat,lng pattern (share links, place links)
                    let match = val.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                    if (!match) {
                      // Try q=lat,lng pattern
                      match = val.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
                    }
                    if (!match) {
                      // Try ll=lat,lng pattern
                      match = val.match(/[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/);
                    }
                    if (match) {
                      setMapLocation((prev) => ({
                        ...prev,
                        latitude: match[1],
                        longitude: match[2],
                      }));
                      message.success(`Coordinates extracted: ${match[1]}, ${match[2]}`);
                    } else {
                      message.error(
                        "Could not extract coordinates. Try copying the link from Google Maps → Share → Copy link."
                      );
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Latitude">
                <Input
                  value={mapLocation.latitude}
                  onChange={(e) =>
                    setMapLocation({ ...mapLocation, latitude: e.target.value })
                  }
                  placeholder="19.0760"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Longitude">
                <Input
                  value={mapLocation.longitude}
                  onChange={(e) =>
                    setMapLocation({
                      ...mapLocation,
                      longitude: e.target.value,
                    })
                  }
                  placeholder="72.8777"
                  size="large"
                />
              </Form.Item>
            </Col>
            {mapLocation.latitude && mapLocation.longitude && (
              <Col xs={24}>
                <Button
                  type="link"
                  icon={<EnvironmentOutlined />}
                  href={`https://www.google.com/maps?q=${mapLocation.latitude},${mapLocation.longitude}`}
                  target="_blank"
                >
                  Verify on Google Maps
                </Button>
              </Col>
            )}
          </Row>
        </Card>

        {/* Languages & Services */}
        <Card className="settings-card" title="Languages & Services">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Languages Spoken">
                <Space orientation="vertical" style={{ width: "100%" }}>
                  <Select
                    placeholder="Add language"
                    size="large"
                    style={{ width: "100%" }}
                    onSelect={(value) =>
                      addToArray(value, languages, setLanguages)
                    }
                    value={null}
                  >
                    <Option value="English">English</Option>
                    <Option value="Hindi">Hindi</Option>
                    <Option value="Marathi">Marathi</Option>
                    <Option value="Tamil">Tamil</Option>
                    <Option value="Telugu">Telugu</Option>
                    <Option value="Bengali">Bengali</Option>
                    <Option value="Gujarati">Gujarati</Option>
                  </Select>
                  <div className="specialty-tags">
                    {languages.map((lang) => (
                      <Tag
                        key={lang}
                        closable
                        onClose={() =>
                          removeFromArray(lang, languages, setLanguages)
                        }
                        color="purple"
                      >
                        {lang}
                      </Tag>
                    ))}
                  </div>
                </Space>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Services Offered">
                <Space orientation="vertical" style={{ width: "100%" }}>
                  <Select
                    placeholder="Add service"
                    size="large"
                    style={{ width: "100%" }}
                    onSelect={(value) =>
                      addToArray(value, services, setServices)
                    }
                    value={null}
                  >
                    <Option value="General Consultation">
                      General Consultation
                    </Option>
                    <Option value="Video Consultation">
                      Video Consultation
                    </Option>
                    <Option value="Home Visit">Home Visit</Option>
                    <Option value="Emergency Care">Emergency Care</Option>
                    <Option value="Diagnosis">Diagnosis</Option>
                    <Option value="Treatment">Treatment</Option>
                  </Select>
                  <div className="specialty-tags">
                    {services.map((service) => (
                      <Tag
                        key={service}
                        closable
                        onClose={() =>
                          removeFromArray(service, services, setServices)
                        }
                        color="cyan"
                      >
                        {service}
                      </Tag>
                    ))}
                  </div>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* About */}
        <Card className="settings-card" title="About You">
          <Form.Item
            label="Professional Summary"
            name="about"
            extra="Tell patients about your expertise and approach to healthcare"
          >
            <TextArea
              placeholder="I am a dedicated healthcare professional with expertise in..."
              rows={4}
              size="large"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Card>

        {/* Save Button */}
        <div className="save-button-container">
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            size="large"
            loading={saving}
          >
            {isNewProfile ? "Create Profile" : "Save Changes"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DoctorSettings;
